import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import Slider from '@mui/material/Slider';
import { getThreeSpeedIndex, getThreeSpeedShortLabel, getThreeSpeedSpeedByIndex, isThreeSpeedPump, snapThreeSpeedSpeed, THREE_SPEED_LOW } from '@/utils/utils';

interface ScheduleDeviceSummaryProps {
  isPump: boolean;
  deviceName: string;
  pumpSpeed?: number | null;
  pumpBrand?: string | null;
  pumpModel?: string | null;
  linkedDeviceNames: string[];
  onPumpSpeedChange?: (speed: number) => void;
}

export const ScheduleDeviceSummary: React.FC<ScheduleDeviceSummaryProps> = ({
  isPump,
  deviceName,
  pumpSpeed,
  pumpBrand,
  pumpModel,
  linkedDeviceNames,
  onPumpSpeedChange,
}) => {
  const { mode } = useSelector((state: RootState) => state.theme);
  const [speed, setSpeed] = useState<number>(pumpSpeed ?? 0);
  const isThreeSpeed = isPump && isThreeSpeedPump(pumpBrand, pumpModel);

  // Keep local slider state in sync when pumpSpeed prop changes
  useEffect(() => {
    setSpeed(pumpSpeed ?? 0);
  }, [pumpSpeed]);

  return (
    <div className="rounded-md border p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="font-semibold">{deviceName}</div>
      </div>
      {isPump ? (
        <div className="flex flex-col gap-3">
          {/* Speed slider (read-only summary) */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Speed</span>
              <span className="text-xs text-gray-500">
                {isThreeSpeed ? getThreeSpeedShortLabel(speed > 0 ? speed : THREE_SPEED_LOW) : `${speed}%`}
              </span>
            </div>
            {isThreeSpeed ? (
              <Slider
                value={getThreeSpeedIndex(snapThreeSpeedSpeed(speed > 0 ? speed : THREE_SPEED_LOW))}
                onChange={(_, newValue) => {
                  if (typeof newValue !== 'number') return;
                  const next = getThreeSpeedSpeedByIndex(newValue);
                  setSpeed(next);
                  onPumpSpeedChange?.(next);
                }}
                onChangeCommitted={(_, newValue) => {
                  if (typeof newValue !== 'number') return;
                  const next = getThreeSpeedSpeedByIndex(newValue);
                  setSpeed(next);
                  onPumpSpeedChange?.(next);
                }}
                min={0}
                max={2}
                step={1}
                marks={[
                  { value: 0, label: 'LOW' },
                  { value: 1, label: 'MEDIUM' },
                  { value: 2, label: 'HIGH' },
                ]}
                valueLabelDisplay="off"
                sx={{
                  color: '#10b981',
                  height: 10,
                  '& .MuiSlider-thumb': {
                    width: 22,
                    height: 22,
                    backgroundColor: '#fff',
                    border: '2px solid currentColor',
                  },
                  '& .MuiSlider-track': {
                    border: 'none',
                  },
                  '& .MuiSlider-rail': {
                    opacity: 0.3,
                  },
                }}
              />
            ) : (
              <>
                <input
                  type="range"
                  min={40}
                  max={100}
                  value={speed}
                  className="w-full"
                  onChange={(e) => {
                    const next = Number(e.target.value);
                    setSpeed(next);
                    onPumpSpeedChange?.(next);
                  }}
                />
                <div className="flex items-center justify-between text-xs mt-1">
                  <span>40%</span>
                  <span>100%</span>
                </div>
              </>
            )}
          </div>
          {/* Linked devices summary */}
          <div className="mt-3">
            <div className="text-sm font-medium mb-1">Linked Devices</div>
            <div className="flex flex-col gap-1 text-sm">
              {linkedDeviceNames.length === 0 ? (
                <span className="text-gray-500">None</span>
              ) : (
                linkedDeviceNames.map((name) => (
                  <span key={name} className={classNames('text-primary-500', {
                    'text-primary-500': mode === 'light',
                    'text-cyan-400': mode === 'dark',
                  })}>{name}</span>
                ))
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="text-sm text-gray-500"></div>
      )}
    </div>
  );
};

export default ScheduleDeviceSummary;
